import React, { FunctionComponent, JSXElementConstructor, ReactElement } from 'react'

import {
  RESPONSE_PENDING,
  RESPONSE_OFFLINE,
  RESPONSE_BADRESPONSE,
  RESPONSE_BADDATA,
  RESPONSE_UNAUTHORIZED,
  RESPONSE_NOTFOUND,
  RESPONSE_FORBIDDEN
} from 'front-end-core/constants/responses'

import Error from './Error'
import Offline from './Offline'
import Unauthorized from './Unauthorized'
import Forbidden from './Forbidden'
import { groupBy, includes, isUndefined } from 'libraries/lodash'
import { SkeletonType } from 'types/Skeleton'
import Loading from './Loading'

// loading skeletons
import PrecedentsTiledMenuSkeleton from 'components/tiled-menu/PrecedentsTiledMenuSkeleton'
import LegislationAccordionSkeletonContainer from 'redux/containers/components/accordion/LegislationAccordionSkeletonContainer'
import QaAccordionSkeletonContainer from 'redux/containers/components/accordion/QaAccordionSkeletonContainer'
import LegislationTiledMenuSkeletonContainer from 'redux/containers/components/tiled-menu/LegislationTiledMenuSkeletonContainer'
import TiledMenuSkeletonContainer from 'redux/containers/components/tiled-menu/TiledMenuSkeletonContainer'
import DashboardSkeletonContainer from 'redux/containers/pages/dashboard/DashboardSkeletonContainer'
import SearchResultsSkeletonContainer from 'redux/containers/pages/search/SearchResultsSkeletonContainer'

const skeletonMap: Record<SkeletonType, JSX.Element> = {
  'legislation-tiled-menu': <LegislationTiledMenuSkeletonContainer/>,
  'precedent-tiled-menu': <PrecedentsTiledMenuSkeleton/>,
  'tiled-menu': <TiledMenuSkeletonContainer/>,
  'legislation-accordion': <LegislationAccordionSkeletonContainer />,
  'qa-accordion': <QaAccordionSkeletonContainer/>,
  'dashboard': <DashboardSkeletonContainer />,
  'search-results': <SearchResultsSkeletonContainer />
}

const ResponseConsumer: FunctionComponent<ResponseConsumerProps> = ({
  pendingComponent = Loading,
  baddataComponent = Error,
  badresponseComponent = Error,
  notfoundComponent = Error,
  offlineComponent = Offline,
  unauthorizedComponent = Unauthorized,
  forbiddenComponent = Forbidden,
  baddataProps = { title: 'Data loading error' },
  badresponseProps = { title: 'Data loading error' },
  notfoundProps = { title: 'Not found' },
  offlineProps = { title: 'Not available offline' },
  unauthorizedProps = { title: 'Not logged in' },
  forbiddenProps = { title: 'Not authorised' },
  content,
  response,
  responses = [],
  handleOnBadData,
  skeleton
}) => {
  const renderStatuses = (statuses: string[]) => {
    const grouped = groupBy(statuses)
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_PENDING)) {
      if(skeleton && skeletonMap[skeleton]) {
        return skeletonMap[skeleton];
      }
      else if(pendingComponent) {
        return <Render component={pendingComponent} />
      }
    }
    // Don't show skeleton placeholders if bad data response
    handleOnBadData && handleOnBadData()

    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_BADDATA)) {
      return <Render component={baddataComponent} props={baddataProps} />
    }
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_BADRESPONSE)) {
      return <Render component={badresponseComponent} props={badresponseProps} />
    }
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_NOTFOUND)) {
      return <Render component={notfoundComponent} props={notfoundProps} />
    }
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_OFFLINE)) {
      return <Render component={offlineComponent} props={offlineProps} />
    }
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_UNAUTHORIZED)) {
      return <Render component={unauthorizedComponent} props={unauthorizedProps} />
    }
    if (Object.prototype.hasOwnProperty.call(grouped, RESPONSE_FORBIDDEN)) {
      return <Render component={forbiddenComponent} props={forbiddenProps} />
    }
    return null
  }

  const renderError = (e: string) => {
    console.error(e)
    return <Render component={Error} />
  }

  try {
    const allResponses: Response[] = responses.concat(
      isUndefined(response) ? [] : [response]
    )
    const notOk: any = allResponses.reduce(
      (a: any, status) => (!responseOk(status) ? [...a, status] : a),
      []
    )
    return notOk.length ? renderStatuses(notOk) : content()
  } catch (e) {
    return renderError(e)
  }
}

const Render: FunctionComponent<RenderProps> = ({ component: Component, props }: RenderProps): React.ReactElement| null =>
  !Component ? null : <Component {...props} />

const responseOk = (status: any) => {
  return !includes(
    [
      RESPONSE_PENDING,
      RESPONSE_OFFLINE,
      RESPONSE_BADRESPONSE,
      RESPONSE_BADDATA,
      RESPONSE_NOTFOUND,
      RESPONSE_UNAUTHORIZED,
      RESPONSE_FORBIDDEN
    ],
    status
  )
}

interface RenderProps {
  component: JSXElementConstructor<any>;
  props?: any;
}

interface TitleProps {
  title: string;
}

type Response = string | number | boolean | object | Array<any>

interface ResponseConsumerProps {
  pendingComponent?: JSXElementConstructor<any>;
  baddataProps?: TitleProps;
  baddataComponent?: JSXElementConstructor<any>;
  badresponseProps?: TitleProps;
  badresponseComponent?: JSXElementConstructor<any>;
  notfoundProps?: TitleProps;
  notfoundComponent?: JSXElementConstructor<any>;
  offlineProps?: TitleProps;
  offlineComponent?: JSXElementConstructor<any>;
  unauthorizedProps?: TitleProps;
  unauthorizedComponent?: JSXElementConstructor<any>;
  forbiddenProps?: TitleProps;
  forbiddenComponent?: JSXElementConstructor<any>;
  response?: Response;
  responses?: Response[];
  content?: () => ReactElement<any>;
  handleOnBadData?: () => void
  skeleton?: SkeletonType
}

export default ResponseConsumer
